﻿let _profUserId = 'default'; try { require('@react-native-async-storage/async-storage').default.getItem('userToken').then(function(t){if(t)_profUserId=t.slice(0,16);}).catch(function(){}); } catch(e) {}
import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  Image,
  ActivityIndicator,
  Share,
  Linking,
  Alert,
  Animated,
  RefreshControl,
  Dimensions,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
// expo-print converts our HTML offer letter into a real PDF. expo-sharing
// opens the system share sheet so the user can save, AirDrop, email, or
// print it. Both are part of the standard managed-workflow Expo SDK so no
// native linking is required.
import * as Print from 'expo-print';
import * as Sharing from 'expo-sharing';
import { UNIFIED_JOB_DATA, getOffersForDetails, getJobById } from '../../services/UnifiedJobData';
import { apiFetch } from '../../utils/apiClient';

// === API CONFIG ===
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
// FIXED: Safe context imports with fallbacks
let useProfessional, useProfessionalOnboarding, useGeneral;
try {
  const professionalModule = require('../../context/ProfessionalContext');
  useProfessional = professionalModule.useProfessional;
} catch (error) {
  console.warn('⚠️ ProfessionalContext not found, using mock');
  useProfessional = () => ({
    userData: {},
    subscriptionData: {},
    profileStats: {},
    preferences: {},
    careerData: {},
    saveUserData: async () => {},
    savePreferences: async () => {},
    saveCareerData: async () => {},
    updateProfileCompletion: async () => {},
    getProfileCompletionTips: () => [],
    isPremiumUser: () => false,
    hasFeature: () => false,
    trackUserAction: () => {},
  });
}

try {
  const onboardingModule = require('../../context/ProfessionalOnboardingContext');
  useProfessionalOnboarding = onboardingModule.useProfessionalOnboarding;
} catch (error) {
  console.warn('⚠️ ProfessionalOnboardingContext not found, using mock');
  useProfessionalOnboarding = () => ({
    profileData: {},
    scoring: {},
    aiMatching: {},
    updateProfileData: async () => {},
    runAIProfileAnalysis: async () => {},
    calculateOverallScore: () => 75,
    isLoading: false,
    error: null,
  });
}

try {
  const generalModule = require('../../context/GeneralContext');
  useGeneral = generalModule.useGeneral;
} catch (error) {
  console.warn('⚠️ GeneralContext not found, using mock');
  useGeneral = () => ({
    showToast: () => {},
    trackUserAction: () => {},
  });
}

const { width, height } = Dimensions.get('window');

export default function OfferDetailsScreen({ navigation, route }) {
  // === Live API Data ===
  const [userId, setUserId] = React.useState('default');
  React.useEffect(() => {
    AsyncStorage.getItem('userToken').then(t => { if (t) setUserId(t.slice(0, 16)); }).catch(() => {});
  }, []);
  React.useEffect(() => {
    if (userId === 'default') return;
    const _ck = `professional_${_profUserId}_offerdetails`;
    apiFetch('/api/data/job_offers?sort=created_at&order=desc&limit=20').then(d => {
      if (d && !d.error) {
        const arr = Array.isArray(d) ? d : (d.data || d.results || []);
        AsyncStorage.setItem(_ck, JSON.stringify(arr)).catch(() => {});
      }
    }).catch(() => {});
  }, [userId]);

  // FIXED: Safe context usage with fallbacks
  const {
    userData = {},
    subscriptionData = {},
    profileStats = {},
    preferences = {},
    careerData = {},
    saveUserData,
    savePreferences,
    saveCareerData,
    updateProfileCompletion,
    getProfileCompletionTips,
    isPremiumUser,
    hasFeature,
    trackUserAction,
  } = useProfessional() || {};

  const {
    profileData = {},
    scoring = {},
    aiMatching = {},
    updateProfileData,
    runAIProfileAnalysis,
    calculateOverallScore,
    isLoading: onboardingLoading,
    error: onboardingError
  } = useProfessionalOnboarding() || {};

  const { showToast, trackUserAction: trackGeneralAction } = useGeneral() || {};

  // States
  const [isLoading, setIsLoading] = useState(true);
  const [offer, setOffer] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Animation values
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(50)).current;

  // Initialize animations
  useEffect(() => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 800,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 800,
        useNativeDriver: true,
      }),
    ]).start();
  }, []);

  // UNIFIED: Load offer details with consistent data structure
  useEffect(() => {
    setIsLoading(true);
    setError(null);
    setOffer(null);

    // Enhanced parameter extraction with unified data support
    const extractOfferId = () => {
      const params = route.params || {};
      console.log('📋 UNIFIED ROUTE PARAMS:', JSON.stringify(params, null, 2));
      
      // Try multiple parameter names for maximum compatibility
      const possibleIds = [
        params.offerId,
        params.id,
        params.jobId,
        params.applicationId,
        params.offer?.id,
        params.offer?.offerId,
        params.offer?.jobId,
        params.job?.id,
        params.job?.offerId,
        params.job?.jobId,
        params.data?.id,
        params.data?.offerId,
        params.item?.id,
      ].filter(Boolean);
      
      console.log('🔍 UNIFIED IDS FOUND:', possibleIds);
      return possibleIds[0];
    };

    const offerId = extractOfferId();
    
    if (offerId) {
      loadOfferDetails(offerId);
    } else {
      console.error('❌ No offer ID found in route params');
      setError('No offer ID provided');
      setIsLoading(false);
    }
  }, [route.params]);

  // UNIFIED: Load offer details with comprehensive data sources
  const loadOfferDetails = async (offerId) => {
    try {
      console.log('🔄 UNIFIED LOADING: Loading offer details for ID:', offerId);
      
      if (!offerId) {
        throw new Error('Invalid offer ID');
      }

      // Priority 1: Try to load from route params (if complete offer object was passed)
      const routeOffer = route.params?.offer || route.params?.job || route.params?.data || route.params?.item;
      if (routeOffer && (
        routeOffer.id === offerId || 
        routeOffer.offerId === offerId || 
        routeOffer.jobId === offerId ||
        routeOffer.applicationId === offerId
      )) {
        console.log('✅ UNIFIED: Using offer data from route params');
        setOffer(routeOffer);
        setIsLoading(false);
        return;
      }

      // Priority 2: Try to load from unified data structure
      const unifiedOffer = getJobById(offerId);
      if (unifiedOffer) {
        console.log('✅ UNIFIED: Using offer data from unified structure');
        // Map to OfferDetails format
        const offerDetailsData = {
          ...unifiedOffer,
          position: unifiedOffer.title,
          logo: unifiedOffer.companyLogo,
          status: unifiedOffer.offerStatus,
        };
        setOffer(offerDetailsData);
        setIsLoading(false);
        return;
      }

      // Priority 3: Try to load from AsyncStorage
      try {
        const storedOffers = await AsyncStorage.getItem(`professional_${_profUserId}_jobOffers`);
        if (storedOffers) {
          const offers = JSON.parse(storedOffers);
          const storedOffer = offers.find(offer => 
            offer.id === offerId || 
            offer.offerId === offerId || 
            offer.jobId === offerId ||
            offer.applicationId === offerId
          );
          if (storedOffer) {
            console.log('✅ UNIFIED: Using offer data from AsyncStorage');
            setOffer(storedOffer);
            setIsLoading(false);
            return;
          }
        }
      } catch (storageError) {
        console.warn('⚠️ Failed to load from AsyncStorage:', storageError.message);
      }

      // Priority 4: Try the real API — last resort for IDs not in route/unified/storage.
      // The canonical endpoint is /api/data/job_offers (underscore). The hyphenated
      // variant returns 404 against the real PostgreSQL-backed handler.
      try {
        const endpoints = [
          `/api/data/job_offers/${encodeURIComponent(offerId)}`,
          `/api/data/job_offers?id=${encodeURIComponent(offerId)}`,
        ];
        for (const ep of endpoints) {
          try {
            const apiRes = await apiFetch(ep);
            if (apiRes && !apiRes.error) {
              // Endpoint may return a single record or {data: [row]} for the query variant
              const raw = apiRes.data || apiRes.record || apiRes;
              const row = Array.isArray(raw) ? raw[0] : raw;
              if (row && (row.id != null || row.offerId != null)) {
                console.log('✅ UNIFIED: Using offer data from API endpoint', ep);
                // Map raw DB shape to the UI shape this screen expects
                const salaryNum = Number(row.offered_salary || row.salary) || 0;
                const cur = row.salary_currency || 'GBP';
                const sym = cur === 'GBP' ? '£' : cur === 'USD' ? '$' : cur === 'EUR' ? '€' : '';
                setOffer({
                  ...row,
                  id:        row.id,
                  offerId:   row.id,
                  jobId:     row.job_id,
                  applicationId: row.job_application_id,
                  position:  row.position || row.title || row.job_title,
                  title:     row.position || row.title || row.job_title,
                  company:   row.company_name || row.employer_name || '',
                  location:  row.location || '',
                  salary:    salaryNum ? `${sym}${salaryNum.toLocaleString()}` : '',
                  salaryRaw: salaryNum,
                  startDate: row.start_date,
                  deadline:  row.deadline,
                  status:    row.status || row.offerStatus || row.offer_status,
                  logo:      row.logo || row.companyLogo || row.company_logo,
                  contractType: row.contract_type || 'Permanent',
                  workType:  row.work_type || 'Hybrid',
                  benefits:  row.benefits || '',
                  notes:     row.notes || '',
                });
                setIsLoading(false);
                return;
              }
            }
          } catch (epErr) {
            // try next endpoint
          }
        }
      } catch (apiError) {
        console.warn('⚠️ API fallback failed:', apiError.message);
      }

      // Priority 5: Truly not found anywhere. Use a friendlier error message
      // for the common case where this is a stale mock notification.
      const isMockShape = /^(offer_|app_|mock_|sample_|fake_)/i.test(String(offerId));
      throw new Error(
        isMockShape
          ? 'This offer is no longer available. It may have been withdrawn or you may be opening an outdated notification.'
          : `Offer with ID ${offerId} not found in any data source`
      );
      
    } catch (error) {
      console.error('❌ UNIFIED ERROR: Failed to load offer details:', error);
      setError(error.message || 'Failed to load offer details');
      setIsLoading(false);
    }
  };

  // Handle refresh
  const onRefresh = async () => {
    setRefreshing(true);
    try {
      const offerId = route.params?.offerId || route.params?.id || route.params?.jobId;
      if (offerId) {
        await loadOfferDetails(offerId);
      }
    } catch (error) {
      console.error('Error refreshing:', error);
    } finally {
      setRefreshing(false);
    }
  };

  // Get status color based on offer status
  const getStatusColor = (status) => {
    switch (status) {
      case 'Pending':
        return '#f59e0b'; // Orange
      case 'Accepted':
        return '#10b981'; // Green
      case 'Rejected':
        return '#dc2626'; // Red
      case 'Expired':
        return '#64748B'; // Gray
      default:
        return '#64748B'; // Default gray
    }
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'Pending':
        return 'time-outline';
      case 'Accepted':
        return 'checkmark-circle-outline';
      case 'Rejected':
        return 'close-circle-outline';
      case 'Expired':
        return 'alert-circle-outline';
      default:
        return 'help-circle-outline';
    }
  };

  // ENHANCED: Share offer with better formatting
  const shareOffer = async () => {
    if (!offer) return;

    try {
      const shareMessage = `🎉 Job Offer Received!

Position: ${offer.position || offer.title}
Company: ${offer.company}
Location: ${offer.location}
Salary: ${offer.salary}
Status: ${offer.status || offer.offerStatus}

${(offer.status === 'Pending' || offer.offerStatus === 'Pending') ? `Deadline: ${offer.deadline}` : ''}

#JobOffer #Career #${offer.company.replace(/\s+/g, '')}`;

      await Share.share({
        message: shareMessage,
        url: `https://jobeng.ai/offers/${offer.id}`,
        title: `Job Offer - ${offer.position || offer.title} at ${offer.company}`,
      });

      // Track share action
      if (trackUserAction && typeof trackUserAction === 'function') {
        trackUserAction('offer_shared', {
          offerId: offer.id,
          position: offer.position || offer.title,
          company: offer.company,
        });
      }
    } catch (error) {
      console.error('Error sharing offer:', error);
      Alert.alert('Error', 'Failed to share offer. Please try again.');
    }
  };

  // ENHANCED: Contact person with better options
  const contactPerson = () => {
    if (!offer || !offer.contactPerson) {
      Alert.alert('Error', 'Contact information not available.');
      return;
    }

    const contact = offer.contactPerson;
    
    Alert.alert(
      `Contact ${contact.name}`,
      `${contact.role}\n${contact.email}\n${contact.phone}`,
      [
        {
          text: 'Send Email',
          onPress: () => {
            Linking.openURL(`mailto:${contact.email}?subject=Regarding ${offer.position || offer.title} Offer`);
            if (trackUserAction && typeof trackUserAction === 'function') {
              trackUserAction('offer_contact_email', { offerId: offer.id });
            }
          },
        },
        {
          text: 'Call',
          onPress: () => {
            Linking.openURL(`tel:${contact.phone}`);
            if (trackUserAction && typeof trackUserAction === 'function') {
              trackUserAction('offer_contact_call', { offerId: offer.id });
            }
          },
        },
        {
          text: 'Cancel',
          style: 'cancel',
        },
      ]
    );
  };

  // ENHANCED: Accept offer with better UX
  // ─── PDF OFFER LETTER ─────────────────────────────────────────────────
  // Builds a professionally-formatted HTML offer letter from the offer's
  // fields, renders it to a real PDF via expo-print, then opens the system
  // share sheet so the candidate can save, email, print, or AirDrop the file.
  // Falls back to Print.printAsync (in-app preview) if the device has no
  // share capability (e.g. Android emulator without an installed Drive app).
  const buildOfferLetterHTML = (o) => {
    const fmt = (iso) => {
      if (!iso) return 'To be confirmed';
      try { return new Date(iso).toLocaleDateString('en-GB', { day:'numeric', month:'long', year:'numeric' }); }
      catch { return String(iso); }
    };
    const today = new Date().toLocaleDateString('en-GB', { day:'numeric', month:'long', year:'numeric' });
    const safe = (s) => String(s == null ? '' : s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
    const benefits = Array.isArray(o.benefits)
      ? o.benefits
      : (typeof o.benefits === 'string' && o.benefits.trim() ? o.benefits.split(/[\n,;]+/).map(s => s.trim()).filter(Boolean) : []);
    const candidateName = o.candidate_name || o.candidateName || o.name || 'Candidate';
    const companyName   = o.company || o.company_name || o.employer_name || 'The Company';
    const position      = o.position || o.title || o.job_title || 'Position';
    const salary        = o.salary || (o.offered_salary != null ? `£${Number(o.offered_salary).toLocaleString()}` : 'To be discussed');
    const location      = o.location || 'To be confirmed';
    const workType      = o.workType || o.work_type || 'Hybrid';
    const contractType  = o.contractType || o.contract_type || 'Permanent';
    const startDate     = fmt(o.startDate || o.start_date);
    const responseBy    = fmt(o.deadline);
    const logoBlock     = o.companyLogo || o.logo
      ? `<img src="${safe(o.companyLogo || o.logo)}" alt="logo" style="height:60px;max-width:200px;object-fit:contain"/>`
      : `<div style="font-size:24px;font-weight:700;color:#0e4e96">${safe(companyName)}</div>`;

    return `<!DOCTYPE html>
<html><head><meta charset="utf-8"/>
<style>
  @page { margin: 28mm 22mm; }
  body { font-family: -apple-system, "Helvetica Neue", Helvetica, Arial, sans-serif; color:#1e293b; line-height:1.55; font-size:11pt; }
  .header { display:flex; align-items:center; justify-content:space-between; border-bottom:2px solid #0e4e96; padding-bottom:14px; margin-bottom:24px; }
  .header .meta { text-align:right; font-size:9pt; color:#64748b; }
  .title { color:#0e4e96; font-size:20pt; font-weight:700; margin:18px 0 4px 0; letter-spacing:-0.5px; }
  .subtitle { color:#64748b; font-size:10pt; margin-bottom:24px; }
  h2 { color:#0e4e96; font-size:12pt; margin:22px 0 8px 0; padding-bottom:4px; border-bottom:1px solid #e2e8f0; }
  table.terms { width:100%; border-collapse:collapse; margin-bottom:8px; }
  table.terms td { padding:6px 0; vertical-align:top; }
  table.terms td:first-child { color:#64748b; font-weight:500; width:38%; }
  table.terms td:last-child  { color:#1e293b; font-weight:600; }
  ul.benefits { padding-left:18px; margin:6px 0 12px 0; }
  ul.benefits li { padding:2px 0; }
  .signature { margin-top:40px; display:flex; gap:60px; }
  .sig-block { flex:1; }
  .sig-line { border-top:1px solid #94a3b8; margin-top:50px; padding-top:6px; font-size:9pt; color:#64748b; }
  .footer { margin-top:40px; padding-top:14px; border-top:1px solid #e2e8f0; font-size:8.5pt; color:#94a3b8; text-align:center; }
  .accent { color:#0e4e96; font-weight:600; }
</style></head>
<body>
  <div class="header">
    ${logoBlock}
    <div class="meta">
      <div><strong>${safe(companyName)}</strong></div>
      <div>Issued: ${today}</div>
      <div>Reference: OFFER-${safe(o.offerId || o.id || 'XXX')}</div>
    </div>
  </div>

  <div class="title">Offer of Employment</div>
  <div class="subtitle">Private &amp; Confidential</div>

  <p>${today}</p>
  <p>Dear ${safe(candidateName)},</p>

  <p>
    On behalf of <span class="accent">${safe(companyName)}</span>, I am delighted to formally offer you the position of
    <span class="accent">${safe(position)}</span>. We were impressed throughout the recruitment process, and we look forward
    to having you join the team.
  </p>

  <h2>Position &amp; Terms</h2>
  <table class="terms">
    <tr><td>Position</td><td>${safe(position)}</td></tr>
    <tr><td>Reporting location</td><td>${safe(location)}</td></tr>
    <tr><td>Work arrangement</td><td>${safe(workType)}</td></tr>
    <tr><td>Contract type</td><td>${safe(contractType)}</td></tr>
    <tr><td>Proposed start date</td><td>${safe(startDate)}</td></tr>
  </table>

  <h2>Compensation</h2>
  <table class="terms">
    <tr><td>Annual base salary</td><td>${safe(salary)}</td></tr>
    <tr><td>Pay frequency</td><td>Monthly, on the last working day</td></tr>
    <tr><td>Probation period</td><td>${safe(o.probation_period || '6 months')}</td></tr>
  </table>

  ${benefits.length ? `<h2>Benefits</h2>
  <ul class="benefits">
    ${benefits.map(b => `<li>${safe(b)}</li>`).join('')}
  </ul>` : ''}

  ${o.notes ? `<h2>Additional Notes</h2><p>${safe(o.notes)}</p>` : ''}

  <h2>Next Steps</h2>
  <p>
    To accept this offer please respond by <span class="accent">${safe(responseBy)}</span>. You can accept or decline directly
    in your JobEng dashboard, or reply to this letter in writing. Following acceptance we will send the formal employment
    contract and onboarding details.
  </p>

  <p>
    We very much hope you will join us, and we look forward to your response.
  </p>

  <p>Warm regards,</p>

  <div class="signature">
    <div class="sig-block">
      <div class="sig-line">For and on behalf of ${safe(companyName)}</div>
    </div>
    <div class="sig-block">
      <div class="sig-line">Accepted by ${safe(candidateName)}</div>
    </div>
  </div>

  <div class="footer">
    This document was generated by JobEng on ${today}. Offer reference: OFFER-${safe(o.offerId || o.id || 'XXX')}.
  </div>
</body></html>`;
  };

  const previewOfferLetter = async () => {
    if (!offer) {
      Alert.alert('Nothing to preview', 'The offer has not finished loading.');
      return;
    }
    try {
      const html = buildOfferLetterHTML(offer);
      // printToFileAsync renders the HTML into a real PDF and returns a
      // local file URI. We then hand that to the share sheet so the user
      // gets the standard iOS/Android preview + save flow.
      const { uri } = await Print.printToFileAsync({ html, base64: false });
      const canShare = await Sharing.isAvailableAsync().catch(() => false);
      if (canShare) {
        await Sharing.shareAsync(uri, {
          mimeType: 'application/pdf',
          dialogTitle: `Offer Letter — ${offer.position || offer.title || ''}`,
          UTI: 'com.adobe.pdf',
        });
      } else {
        // Fallback for devices with no sharing capability — show the system
        // print preview, which still lets the user save to PDF / print.
        await Print.printAsync({ html });
      }
    } catch (e) {
      console.warn('previewOfferLetter:', e?.message);
      Alert.alert('Preview failed', e?.message || 'Could not generate the offer letter PDF.');
    }
  };

  const acceptOffer = () => {
    if (!offer) return;

    Alert.alert(
      'Accept Job Offer',
      `Are you sure you want to accept the ${offer.position || offer.title} offer from ${offer.company}?\n\nSalary: ${offer.salary}\nStart Date: ${offer.startDate}`,
      [
        {
          text: 'Cancel',
          style: 'cancel',
        },
        {
          text: 'Accept Offer',
          style: 'default',
          onPress: async () => {
            try {
              setIsLoading(true);

              // Prefer the numeric DB id over a possibly-mock route id.
              // When the offer was opened from JobsOffersScreen the route
              // params often carry BOTH a mock 'id' (e.g. "job_002") AND a
              // real 'offerId' (e.g. "2") — earlier code picked 'id' first
              // which made the PUT go to /api/data/job_offers/job_002 and
              // silently 404. Reverse the priority + reject obvious mock
              // shapes so we don't try to write to a fictional row.
              const rawId = offer.offerId || offer.id;
              const isMock = /^(offer_|app_|mock_|sample_|fake_|job_\d+)/i.test(String(rawId));
              if (isMock) {
                setIsLoading(false);
                Alert.alert(
                  'Sample offer',
                  "This is a sample offer for layout preview — there's nothing real to accept. Once a recruiter sends you a real offer it will appear here with full functionality.",
                  [{ text: 'OK' }]
                );
                return;
              }
              const offerId = rawId;
              const today  = new Date().toISOString().slice(0, 10);
              const nowIso = new Date().toISOString();

              // 1. REAL DB WRITE — flip the job_offers row to accepted.
              //    The recruiter's AcceptedOffersScreen filters by
              //    status='accepted' so this row will appear there as soon as
              //    the recruiter refreshes.
              let dbSaved = false;
              try {
                const r = await apiFetch(`/api/data/job_offers/${offerId}`, {
                  method: 'PUT',
                  body: JSON.stringify({
                    status: 'accepted',
                    accepted_date: today,
                    response_date: nowIso,
                  }),
                });
                // 404 from the generic data handler means the row doesn't
                // exist — this happens when the offer came from a mock
                // data source (UnifiedJobData / ActivityDetailScreen). The
                // isMock regex above catches obvious patterns like job_002
                // but plain numeric ids like "2" sneak past. Treat 404 as
                // "this is a sample offer" rather than showing a confusing
                // generic error.
                if (r?.error && (r?.status === 404 || /not found/i.test(String(r?.message || '')))) {
                  setIsLoading(false);
                  Alert.alert(
                    'Sample offer',
                    'This offer is sample data from the activity feed — there is no matching record in the database to accept. When a real offer is sent to you it will appear in your Job Offers list with full Accept/Decline functionality.',
                    [{ text: 'OK' }]
                  );
                  return;
                }
                dbSaved = !!(r && !r.error);
                console.log('[OfferDetails] accept PUT result:', r);
              } catch (e) { console.warn('acceptOffer PUT:', e?.message); }

              // 2. Bump the applicant pipeline stage so the recruiter view
              //    reflects the candidate moved forward. Application id may
              //    come from the offer row (job_application_id) or by lookup
              //    on candidate_email.
              let appId = offer.applicationId
                       || offer.job_application_id
                       || offer.rawOffer?.job_application_id
                       || null;
              if (!appId && offer.candidate_email) {
                try {
                  const r = await apiFetch(`/api/data/applications?candidate_email=${encodeURIComponent(offer.candidate_email)}&limit=20`);
                  const rows = Array.isArray(r) ? r : (r?.data || []);
                  const match = rows.find(a => offer.jobId && a.job_id === offer.jobId) || rows[0];
                  appId = match?.id || null;
                } catch {}
              }
              if (appId) {
                try {
                  await apiFetch(`/api/recruitment/applicant/${appId}/stage`, {
                    method: 'POST',
                    body: JSON.stringify({
                      stage:  'offer_accepted',
                      status: 'accepted',
                      note:   `Candidate accepted offer for ${offer.position || offer.title}`,
                    }),
                  });
                } catch (e) { console.warn('acceptOffer stage:', e?.message); }
              }

              const updatedOffer = {
                ...offer,
                status: 'Accepted',
                offerStatus: 'Accepted',
                acceptedDate: today,
                responseDate: nowIso,
              };
              setOffer(updatedOffer);

              // 3. Mirror to local cache (legacy storage)
              try {
                await AsyncStorage.setItem(`professional_${_profUserId}_currentOffer`, JSON.stringify(updatedOffer));
                const acceptedOffers = await AsyncStorage.getItem(`professional_${_profUserId}_acceptedOffers`) || '[]';
                const acceptedList = JSON.parse(acceptedOffers);
                acceptedList.push(updatedOffer);
                await AsyncStorage.setItem(`professional_${_profUserId}_acceptedOffers`, JSON.stringify(acceptedList));
              } catch (saveError) {
                console.warn('⚠️ Failed to save accepted offer:', saveError.message);
              }

              if (trackUserAction && typeof trackUserAction === 'function') {
                trackUserAction('offer_accepted', {
                  offerId,
                  position: offer.position || offer.title,
                  company:  offer.company,
                  salary:   offer.salary,
                  db_saved: dbSaved,
                });
              }

              Alert.alert(
                dbSaved ? '🎉 Offer Accepted!' : 'Accepted (offline)',
                dbSaved
                  ? `Congratulations! You have accepted the offer for ${offer.position || offer.title} at ${offer.company}.\n\nThe HR team has been notified and will contact you about next steps.`
                  : `We've recorded your acceptance locally. Please check your connection and refresh so the employer is notified.`,
                [
                  {
                    text: 'View Applications',
                    onPress: () => {
                      try { navigation.navigate('JobApplications'); }
                      catch {
                        try { navigation.navigate('Applications'); }
                        catch { navigation.navigate('JobApplicationsScreen'); }
                      }
                    }
                  },
                  { text: 'OK' }
                ]
              );

              if (showToast && typeof showToast === 'function') {
                showToast(dbSaved ? 'Offer accepted successfully!' : 'Accepted (saved locally)', dbSaved ? 'success' : 'warning');
              }
            } catch (error) {
              console.error('❌ Error accepting offer:', error);
              Alert.alert('Error', 'Failed to accept offer. Please try again.');
            } finally {
              setIsLoading(false);
            }
          },
        },
      ]
    );
  };

  // ENHANCED: Reject offer with reason
  const rejectOffer = () => {
    if (!offer) return;

    Alert.alert(
      'Reject Job Offer',
      `Are you sure you want to reject the ${offer.position || offer.title} offer from ${offer.company}?`,
      [
        {
          text: 'Cancel',
          style: 'cancel',
        },
        {
          text: 'Reject Offer',
          style: 'destructive',
          onPress: async () => {
            try {
              setIsLoading(true);

              // Same numeric-id priority + mock guard as acceptOffer
              const rawId = offer.offerId || offer.id;
              const isMock = /^(offer_|app_|mock_|sample_|fake_|job_\d+)/i.test(String(rawId));
              if (isMock) {
                setIsLoading(false);
                Alert.alert(
                  'Sample offer',
                  "This is a sample offer for layout preview — there's nothing real to reject.",
                  [{ text: 'OK' }]
                );
                return;
              }
              const offerId = rawId;
              const today  = new Date().toISOString().slice(0, 10);
              const nowIso = new Date().toISOString();

              // 1. REAL DB WRITE — flag the offer rejected
              let dbSaved = false;
              try {
                const r = await apiFetch(`/api/data/job_offers/${offerId}`, {
                  method: 'PUT',
                  body: JSON.stringify({
                    status: 'rejected',
                    declined_date: today,
                    response_date: nowIso,
                  }),
                });
                if (r?.error && (r?.status === 404 || /not found/i.test(String(r?.message || '')))) {
                  setIsLoading(false);
                  Alert.alert(
                    'Sample offer',
                    'This offer is sample data — no matching database record to reject. Real offers from recruiters will surface here with full functionality.',
                    [{ text: 'OK' }]
                  );
                  return;
                }
                dbSaved = !!(r && !r.error);
              } catch (e) { console.warn('rejectOffer PUT:', e?.message); }

              // 2. Bump applicant stage so the recruiter pipeline reflects it
              let appId = offer.applicationId
                       || offer.job_application_id
                       || offer.rawOffer?.job_application_id
                       || null;
              if (!appId && offer.candidate_email) {
                try {
                  const r = await apiFetch(`/api/data/applications?candidate_email=${encodeURIComponent(offer.candidate_email)}&limit=20`);
                  const rows = Array.isArray(r) ? r : (r?.data || []);
                  const match = rows.find(a => offer.jobId && a.job_id === offer.jobId) || rows[0];
                  appId = match?.id || null;
                } catch {}
              }
              if (appId) {
                try {
                  await apiFetch(`/api/recruitment/applicant/${appId}/stage`, {
                    method: 'POST',
                    body: JSON.stringify({
                      stage:  'rejected',
                      status: 'declined',
                      note:   `Candidate declined offer for ${offer.position || offer.title}`,
                    }),
                  });
                } catch (e) { console.warn('rejectOffer stage:', e?.message); }
              }

              const updatedOffer = {
                ...offer,
                status: 'Rejected',
                offerStatus: 'Rejected',
                rejectedDate: today,
                responseDate: nowIso,
              };
              setOffer(updatedOffer);

              try {
                await AsyncStorage.setItem(`professional_${_profUserId}_currentOffer`, JSON.stringify(updatedOffer));
              } catch (saveError) {
                console.warn('⚠️ Failed to save rejected offer:', saveError.message);
              }

              if (trackUserAction && typeof trackUserAction === 'function') {
                trackUserAction('offer_rejected', {
                  offerId,
                  position: offer.position || offer.title,
                  company:  offer.company,
                  db_saved: dbSaved,
                });
              }

              Alert.alert(
                'Offer Rejected',
                dbSaved
                  ? `You have rejected the offer for ${offer.position || offer.title} at ${offer.company}.\n\nThe company has been notified.`
                  : `Recorded locally. Refresh to notify the employer.`,
                [{ text: 'OK' }]
              );

              if (showToast && typeof showToast === 'function') {
                showToast(dbSaved ? 'Offer rejected' : 'Rejected (saved locally)', 'info');
              }

            } catch (error) {
              console.error('❌ Error rejecting offer:', error);
              Alert.alert('Error', 'Failed to reject offer. Please try again.');
            } finally {
              setIsLoading(false);
            }
          },
        },
      ]
    );
  };

  // ENHANCED: Negotiate offer with navigation
  const negotiateOffer = () => {
    if (!offer) return;

    Alert.alert(
      'Negotiate Offer',
      `Would you like to negotiate the terms of the ${offer.position || offer.title} offer from ${offer.company}?`,
      [
        {
          text: 'Cancel',
          style: 'cancel',
        },
        {
          text: 'Start Negotiation',
          onPress: () => {
            try {
              // Track negotiation start
              if (trackUserAction && typeof trackUserAction === 'function') {
                trackUserAction('offer_negotiation_started', {
                  offerId: offer.id,
                  position: offer.position || offer.title,
                  company: offer.company,
                });
              }
              
              // Navigate to negotiation screen
              navigation.navigate('OfferNegotiation', { 
                offerId: offer.id,
                offer: offer,
              });
            } catch (navError) {
              console.warn('⚠️ Navigation to OfferNegotiation failed:', navError.message);
              Alert.alert(
                'Negotiation',
                'Negotiation feature is coming soon. Please contact the recruiter directly for now.',
                [
                  { text: 'Contact Recruiter', onPress: contactPerson },
                  { text: 'OK' }
                ]
              );
            }
          },
        },
      ]
    );
  };

  // Calculate days until deadline
  const getDaysUntilDeadline = () => {
    if (!offer || !offer.deadline) return null;
    
    try {
      const deadline = new Date(offer.deadline);
      const now = new Date();
      const diffTime = deadline - now;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      if (diffDays < 0) return 'Expired';
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return '1 day';
      return `${diffDays} days`;
    } catch (error) {
      return null;
    }
  };

  // UNIFIED: Navigate to applications with consistent data
  const navigateToApplications = () => {
    try {
      console.log('🧭 UNIFIED: Navigating to JobApplications');
      navigation.navigate('JobApplications', {
        highlightJobId: offer?.id || offer?.jobId,
        source: 'OfferDetails',
      });
    } catch (error) {
      try {
        navigation.navigate('Applications', {
          highlightJobId: offer?.id || offer?.jobId,
          source: 'OfferDetails',
        });
      } catch (secondError) {
        try {
          navigation.navigate('JobApplicationsScreen', {
            highlightJobId: offer?.id || offer?.jobId,
            source: 'OfferDetails',
          });
        } catch (thirdError) {
          console.error('❌ UNIFIED ERROR: Failed to navigate to applications:', error);
          Alert.alert('Error', 'Unable to open applications screen.');
        }
      }
    }
  };

  // Render offer details
  const renderOfferDetails = () => {
    if (!offer) return null;

    const daysUntilDeadline = getDaysUntilDeadline();
    const currentStatus = offer.status || offer.offerStatus || 'Pending';

    return (
      <ScrollView
        style={styles.contentContainer}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            colors={['#0e4e96']}
            tintColor="#0e4e96"
          />
        }
      >
        <Animated.View
          style={[
            styles.offerCard,
            {
              opacity: fadeAnim,
              transform: [{ translateY: slideAnim }],
            },
          ]}
        >
          <View style={styles.offerHeader}>
            <View style={styles.companyLogoContainer}>
              {(offer.logo || offer.companyLogo) ? (
                <Image 
                  source={{ uri: (offer.logo || offer.companyLogo) || undefined }} 
                  style={styles.companyLogo}
                  onError={() => console.warn('Failed to load company logo')}
                />
              ) : (
                <View style={styles.companyLogoPlaceholder}>
                  <Text style={styles.companyLogoPlaceholderText}>
                    {offer.company.charAt(0)}
                  </Text>
                </View>
              )}
            </View>
            
            <View style={styles.offerHeaderInfo}>
              <Text style={styles.positionTitle}>{offer.position || offer.title}</Text>
              <Text style={styles.companyName}>{offer.company}</Text>
              <Text style={styles.locationText}>{offer.location}</Text>
              
              {offer.type && (
                <View style={styles.jobTypeContainer}>
                  <View style={styles.jobTypeTag}>
                    <Text style={styles.jobTypeText}>{offer.type}</Text>
                  </View>
                  {(offer.remote || offer.isRemote) && (
                    <View style={styles.remoteTag}>
                      <Ionicons name="home" size={12} color="#06b6d4" />
                      <Text style={styles.remoteText}>Remote</Text>
                    </View>
                  )}
                </View>
              )}
            </View>
          </View>

          <View style={styles.statusSection}>
            <View
              style={[
                styles.statusBadge,
                { backgroundColor: getStatusColor(currentStatus) + '20' },
              ]}
            >
              <Ionicons 
                name={getStatusIcon(currentStatus)} 
                size={16} 
                color={getStatusColor(currentStatus)} 
              />
              <Text
                style={[
                  styles.statusText,
                  { color: getStatusColor(currentStatus) },
                ]}
              >
                {currentStatus}
              </Text>
            </View>
            
            {daysUntilDeadline && currentStatus === 'Pending' && (
              <View style={styles.deadlineContainer}>
                <Ionicons name="alarm-outline" size={16} color="#f59e0b" />
                <Text style={styles.deadlineText}>
                  {daysUntilDeadline === 'Today' ? 'Deadline Today!' : 
                   daysUntilDeadline === 'Expired' ? 'Deadline Passed' :
                   `${daysUntilDeadline} left`}
                </Text>
              </View>
            )}
          </View>

          <View style={styles.offerActions}>
            <TouchableOpacity style={styles.actionButton} onPress={shareOffer}>
              <Ionicons name="share-outline" size={20} color="#64748B" />
              <Text style={styles.actionButtonText}>Share</Text>
            </TouchableOpacity>

            <TouchableOpacity style={styles.actionButton} onPress={contactPerson}>
              <Ionicons name="person-outline" size={20} color="#64748B" />
              <Text style={styles.actionButtonText}>Contact</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.actionButton}
              onPress={navigateToApplications}
            >
              <Ionicons name="document-text-outline" size={20} color="#64748B" />
              <Text style={styles.actionButtonText}>Applications</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.offerQuickInfo}>
            <View style={styles.quickInfoItem}>
              <Ionicons name="cash-outline" size={18} color="#10b981" />
              <Text style={styles.quickInfoText}>{offer.salary}</Text>
            </View>
            <View style={styles.quickInfoItem}>
              <Ionicons name="calendar-outline" size={18} color="#0e4e96" />
              <Text style={styles.quickInfoText}>Start: {offer.startDate}</Text>
            </View>
            <View style={styles.quickInfoItem}>
              <Ionicons name="time-outline" size={18} color="#f59e0b" />
              <Text style={styles.quickInfoText}>Deadline: {offer.deadline}</Text>
            </View>
          </View>
        </Animated.View>

        <View style={styles.tabsContainer}>
          <TouchableOpacity
            style={[styles.tab, activeTab === 'details' && styles.activeTab]}
            onPress={() => setActiveTab('details')}
          >
            <Ionicons 
              name="information-circle-outline" 
              size={16} 
              color={activeTab === 'details' ? '#0e4e96' : '#64748B'} 
            />
            <Text
              style={[
                styles.tabText,
                activeTab === 'details' && styles.activeTabText,
              ]}
            >
              Details
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.tab, activeTab === 'benefits' && styles.activeTab]}
            onPress={() => setActiveTab('benefits')}
          >
            <Ionicons 
              name="gift-outline" 
              size={16} 
              color={activeTab === 'benefits' ? '#0e4e96' : '#64748B'} 
            />
            <Text
              style={[
                styles.tabText,
                activeTab === 'benefits' && styles.activeTabText,
              ]}
            >
              Benefits
            </Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={[styles.tab, activeTab === 'contact' && styles.activeTab]}
            onPress={() => setActiveTab('contact')}
          >
            <Ionicons 
              name="person-outline" 
              size={16} 
              color={activeTab === 'contact' ? '#0e4e96' : '#64748B'} 
            />
            <Text
              style={[
                styles.tabText,
                activeTab === 'contact' && styles.activeTabText,
              ]}
            >
              Contact
            </Text>
          </TouchableOpacity>
        </View>

        <View style={styles.tabContent}>
          {activeTab === 'details' && (
            <View>
              <Text style={styles.sectionTitle}>Offer Description</Text>
              <Text style={styles.descriptionText}>{offer.description || offer.jobDescription}</Text>

              {offer.requirements && offer.requirements.length > 0 && (
                <>
                  <Text style={styles.sectionTitle}>Requirements</Text>
                  <View style={styles.requirementsContainer}>
                    {offer.requirements.map((requirement, index) => (
                      <View key={index} style={styles.requirementItem}>
                        <Ionicons name="checkmark-circle" size={16} color="#0e4e96" />
                        <Text style={styles.requirementText}>{requirement}</Text>
                      </View>
                    ))}
                  </View>
                </>
              )}

              {offer.responsibilities && offer.responsibilities.length > 0 && (
                <>
                  <Text style={styles.sectionTitle}>Key Responsibilities</Text>
                  <View style={styles.responsibilitiesContainer}>
                    {offer.responsibilities.map((responsibility, index) => (
                      <View key={index} style={styles.responsibilityItem}>
                        <Ionicons name="arrow-forward" size={16} color="#64748B" />
                        <Text style={styles.responsibilityText}>{responsibility}</Text>
                      </View>
                    ))}
                  </View>
                </>
              )}

              <Text style={styles.sectionTitle}>Additional Details</Text>
              <View style={styles.detailsContainer}>
                <View style={styles.detailItem}>
                  <Text style={styles.detailLabel}>Department:</Text>
                  <Text style={styles.detailValue}>
                    {offer.additionalDetails?.department || 'Not specified'}
                  </Text>
                </View>
                <View style={styles.detailItem}>
                  <Text style={styles.detailLabel}>Reports To:</Text>
                  <Text style={styles.detailValue}>
                    {offer.additionalDetails?.reportsTo || 'Not specified'}
                  </Text>
                </View>
                <View style={styles.detailItem}>
                  <Text style={styles.detailLabel}>Work Schedule:</Text>
                  <Text style={styles.detailValue}>
                    {offer.additionalDetails?.workSchedule || 'Standard hours'}
                  </Text>
                </View>
                <View style={styles.detailItem}>
                  <Text style={styles.detailLabel}>Probation Period:</Text>
                  <Text style={styles.detailValue}>
                    {offer.additionalDetails?.probationPeriod || 'Standard'}
                  </Text>
                </View>
                <View style={styles.detailItem}>
                  <Text style={styles.detailLabel}>Overtime Policy:</Text>
                  <Text style={styles.detailValue}>
                    {offer.additionalDetails?.overtimePolicy || 'As per company policy'}
                  </Text>
                </View>
                <View style={styles.detailItem}>
                  <Text style={styles.detailLabel}>Travel Requirements:</Text>
                  <Text style={styles.detailValue}>
                    {offer.additionalDetails?.travelRequirements || 'Minimal'}
                  </Text>
                </View>
              </View>
            </View>
          )}

          {activeTab === 'benefits' && (
            <View>
              <Text style={styles.sectionTitle}>Benefits Package</Text>
              <View style={styles.benefitsContainer}>
                {offer.benefits && offer.benefits.map((benefit, index) => (
                  <View key={index} style={styles.benefitItem}>
                    <Ionicons
                      name="checkmark-circle"
                      size={18}
                      color="#10b981"
                    />
                    <Text style={styles.benefitText}>{benefit}</Text>
                  </View>
                ))}
              </View>
              
              {offer.matchScore && (
                <View style={styles.matchScoreSection}>
                  <Text style={styles.sectionTitle}>Job Match Analysis</Text>
                  <View style={styles.matchScoreContainer}>
                    <View style={styles.matchScoreCircle}>
                      <Text style={styles.matchScoreText}>{offer.matchScore}%</Text>
                    </View>
                    <View style={styles.matchScoreInfo}>
                      <Text style={styles.matchScoreTitle}>Profile Match</Text>
                      <Text style={styles.matchScoreDescription}>
                        {offer.matchScore >= 90 ? 'Excellent match for your profile' :
                         offer.matchScore >= 80 ? 'Very good match for your skills' :
                         offer.matchScore >= 70 ? 'Good match with growth potential' :
                         'Consider if this aligns with your goals'}
                      </Text>
                    </View>
                  </View>
                </View>
              )}
            </View>
          )}

          {activeTab === 'contact' && (
            <View>
              <Text style={styles.sectionTitle}>Contact Person</Text>
              <View style={styles.contactPersonContainer}>
                <View style={styles.contactAvatarContainer}>
                  {offer.contactPerson?.avatar ? (
                    <Image
                      source={{ uri: (offer.contactPerson.avatar && offer.contactPerson.avatar.startsWith('http')) ? offer.contactPerson.avatar : undefined }}
                      style={styles.contactAvatar}
                      onError={() => console.warn('Failed to load contact avatar')}
                    />
                  ) : (
                    <View style={styles.contactAvatarPlaceholder}>
                      <Text style={styles.contactAvatarPlaceholderText}>
                        {offer.contactPerson?.name?.charAt(0) || offer.recruiterName?.charAt(0) || 'C'}
                      </Text>
                    </View>
                  )}
                </View>
                
                <View style={styles.contactInfo}>
                  <Text style={styles.contactName}>
                    {offer.contactPerson?.name || offer.recruiterName || 'Contact Person'}
                  </Text>
                  <Text style={styles.contactRole}>
                    {offer.contactPerson?.role || 'HR Representative'}
                  </Text>
                  <Text style={styles.contactEmail}>
                    {offer.contactPerson?.email || offer.recruiterEmail || 'Not provided'}
                  </Text>
                  <Text style={styles.contactPhone}>
                    {offer.contactPerson?.phone || offer.recruiterPhone || 'Not provided'}
                  </Text>
                  
                  <View style={styles.contactActions}>
                    <TouchableOpacity
                      style={styles.contactButton}
                      onPress={() => {
                        const email = offer.contactPerson?.email || offer.recruiterEmail;
                        if (email) {
                          Linking.openURL(`mailto:${email}?subject=Regarding ${offer.position || offer.title} Offer`);
                        } else {
                          Alert.alert('Error', 'Email address not available.');
                        }
                      }}
                    >
                      <Ionicons name="mail-outline" size={16} color="#0e4e96" />
                      <Text style={styles.contactButtonText}>Send Email</Text>
                    </TouchableOpacity>
                    
                    <TouchableOpacity
                      style={styles.contactButton}
                      onPress={() => {
                        const phone = offer.contactPerson?.phone || offer.recruiterPhone;
                        if (phone) {
                          Linking.openURL(`tel:${phone}`);
                        } else {
                          Alert.alert('Error', 'Phone number not available.');
                        }
                      }}
                    >
                      <Ionicons name="call-outline" size={16} color="#0e4e96" />
                      <Text style={styles.contactButtonText}>Call</Text>
                    </TouchableOpacity>
                  </View>
                </View>
              </View>
            </View>
          )}
        </View>

        {currentStatus === 'Pending' && (
          <View style={styles.offerResponseContainer}>
            <Text style={styles.responseTitle}>Respond to Offer</Text>
            <Text style={styles.responseDeadline}>
              Please respond by {offer.deadline}
              {daysUntilDeadline && ` (${daysUntilDeadline})`}
            </Text>

            {/* Preview the offer as a formal PDF letter — useful for sharing
                with family / advisors before responding, or saving for
                personal records. Generates client-side from the offer's
                fields, no network round-trip. */}
            <TouchableOpacity
              style={{
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#ffffff',
                borderWidth: 1.5,
                borderColor: '#0e4e96',
                borderRadius: 10,
                paddingVertical: 12,
                marginBottom: 12,
                gap: 8,
              }}
              onPress={previewOfferLetter}
            >
              <MaterialCommunityIcons name="file-pdf-box" size={20} color="#0e4e96" />
              <Text style={{ color: '#0e4e96', fontWeight: '600', fontSize: 15 }}>
                Preview Offer Letter (PDF)
              </Text>
            </TouchableOpacity>

            <View style={styles.responseButtons}>
              <TouchableOpacity
                style={[styles.responseButton, styles.rejectButton]}
                onPress={rejectOffer}
              >
                <Ionicons name="close-outline" size={20} color="#ffffff" />
                <Text style={styles.rejectButtonText}>Reject</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.responseButton, styles.negotiateButton]}
                onPress={negotiateOffer}
              >
                <Ionicons name="chatbubble-outline" size={20} color="#ffffff" />
                <Text style={styles.negotiateButtonText}>Negotiate</Text>
              </TouchableOpacity>

              <TouchableOpacity
                style={[styles.responseButton, styles.acceptButton]}
                onPress={acceptOffer}
              >
                <Ionicons name="checkmark-outline" size={20} color="#ffffff" />
                <Text style={styles.acceptButtonText}>Accept</Text>
              </TouchableOpacity>
            </View>
          </View>
        )}

        <View style={styles.bottomPadding} />
      </ScrollView>
    );
  };

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />

      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Ionicons name="arrow-back" size={24} color="#FFFFFF" />
        </TouchableOpacity>

        <Text style={styles.headerTitle}>Offer Details</Text>

        <TouchableOpacity
          style={styles.headerButton}
          onPress={() => {
            Alert.alert('Options', 'Choose an action', [
              {
                text: 'Download Offer Letter',
                onPress: () => {
                  Alert.alert('Info', 'Download feature is coming soon.');
                },
              },
              {
                text: 'View Applications',
                onPress: navigateToApplications,
              },
              {
                text: 'Share Offer',
                onPress: shareOffer,
              },
              {
                text: 'Report Issue',
                onPress: () => {
                  Alert.alert('Report Issue', 'Please describe the issue you encountered.');
                },
              },
              {
                text: 'Cancel',
                style: 'cancel',
              },
            ]);
          }}
        >
          <Ionicons name="ellipsis-vertical" size={24} color="#FFFFFF" />
        </TouchableOpacity>
      </View>

      {/* Content */}
      {isLoading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#0e4e96" />
          <Text style={styles.loadingText}>Loading offer details...</Text>
        </View>
      ) : error ? (
        <View style={styles.errorContainer}>
          <Ionicons name="alert-circle-outline" size={64} color="#dc2626" />
          <Text style={styles.errorTitle}>Unable to Load Offer</Text>
          <Text style={styles.errorDescription}>{error}</Text>
          <TouchableOpacity
            style={styles.retryButton}
            onPress={() => {
              const offerId = route.params?.offerId || route.params?.id || route.params?.jobId;
              if (offerId) {
                loadOfferDetails(offerId);
              } else {
                navigation.goBack();
              }
            }}
          >
            <Text style={styles.retryButtonText}>Try Again</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.goBackButton}
            onPress={() => navigation.goBack()}
          >
            <Text style={styles.goBackButtonText}>Go Back</Text>
          </TouchableOpacity>
        </View>
      ) : (
        renderOfferDetails()
      )}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#0e4e96',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 16,
    backgroundColor: '#0e4e96',
  },
  backButton: {
    padding: 8,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  headerButton: {
    padding: 8,
  },
  contentContainer: {
    flex: 1,
  },
  offerCard: {
    backgroundColor: '#ffffff',
    margin: 16,
    borderRadius: 16,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 5,
  },
  offerHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 20,
  },
  companyLogoContainer: {
    marginRight: 16,
  },
  companyLogo: {
    width: 64,
    height: 64,
    borderRadius: 12,
  },
  companyLogoPlaceholder: {
    width: 64,
    height: 64,
    borderRadius: 12,
    backgroundColor: '#0e4e96',
    alignItems: 'center',
    justifyContent: 'center',
  },
  companyLogoPlaceholderText: {
    fontSize: 24,
    fontWeight: '600',
    color: '#ffffff',
  },
  offerHeaderInfo: {
    flex: 1,
  },
  positionTitle: {
    fontSize: 22,
    fontWeight: '700',
    color: '#1f2937',
    marginBottom: 4,
  },
  companyName: {
    fontSize: 18,
    fontWeight: '500',
    color: '#0e4e96',
    marginBottom: 4,
  },
  locationText: {
    fontSize: 16,
    color: '#64748B',
    marginBottom: 8,
  },
  jobTypeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  jobTypeTag: {
    backgroundColor: '#0e4e96' + '20',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    marginRight: 8,
  },
  jobTypeText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#0e4e96',
  },
  remoteTag: {
    backgroundColor: '#06b6d4' + '20',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
    flexDirection: 'row',
    alignItems: 'center',
  },
  remoteText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#06b6d4',
    marginLeft: 4,
  },
  statusSection: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
  },
  statusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
  },
  statusText: {
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 6,
  },
  deadlineContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f59e0b' + '20',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
  },
  deadlineText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#f59e0b',
    marginLeft: 6,
  },
  offerActions: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginBottom: 20,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: '#e5e7eb',
  },
  actionButton: {
    alignItems: 'center',
    padding: 8,
  },
  actionButtonText: {
    fontSize: 12,
    color: '#64748B',
    marginTop: 4,
    fontWeight: '500',
  },
  offerQuickInfo: {
    marginBottom: 20,
  },
  quickInfoItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  quickInfoText: {
    fontSize: 16,
    fontWeight: '500',
    color: '#1f2937',
    marginLeft: 12,
  },
  tabsContainer: {
    flexDirection: 'row',
    backgroundColor: '#f3f4f6',
    borderRadius: 12,
    padding: 4,
    marginHorizontal: 16,
    marginBottom: 20,
  },
  tab: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: 8,
  },
  activeTab: {
    backgroundColor: '#ffffff',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  tabText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#64748B',
    marginLeft: 6,
  },
  activeTabText: {
    color: '#0e4e96',
    fontWeight: '600',
  },
  tabContent: {
    paddingHorizontal: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1f2937',
    marginBottom: 12,
    marginTop: 8,
  },
  descriptionText: {
    fontSize: 16,
    color: '#374151',
    lineHeight: 24,
    marginBottom: 20,
  },
  requirementsContainer: {
    marginBottom: 20,
  },
  requirementItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  requirementText: {
    fontSize: 15,
    color: '#374151',
    marginLeft: 12,
    flex: 1,
  },
  responsibilitiesContainer: {
    marginBottom: 20,
  },
  responsibilityItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  responsibilityText: {
    fontSize: 15,
    color: '#374151',
    marginLeft: 12,
    flex: 1,
    lineHeight: 22,
  },
  detailsContainer: {
    backgroundColor: '#f8fafc',
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
  },
  detailItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  detailLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#64748B',
    flex: 1,
  },
  detailValue: {
    fontSize: 14,
    color: '#1f2937',
    flex: 2,
    textAlign: 'right',
  },
  benefitsContainer: {
    marginBottom: 20,
  },
  benefitItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    backgroundColor: '#f0fdf4',
    padding: 12,
    borderRadius: 8,
  },
  benefitText: {
    fontSize: 15,
    color: '#374151',
    marginLeft: 12,
    flex: 1,
  },
  matchScoreSection: {
    backgroundColor: '#f8fafc',
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
  },
  matchScoreContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  matchScoreCircle: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: '#0e4e96',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 16,
  },
  matchScoreText: {
    fontSize: 18,
    fontWeight: '700',
    color: '#ffffff',
  },
  matchScoreInfo: {
    flex: 1,
  },
  matchScoreTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#1f2937',
    marginBottom: 4,
  },
  matchScoreDescription: {
    fontSize: 14,
    color: '#64748B',
    lineHeight: 20,
  },
  contactPersonContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: '#f8fafc',
    borderRadius: 12,
    padding: 16,
  },
  contactAvatarContainer: {
    marginRight: 16,
  },
  contactAvatar: {
    width: 60,
    height: 60,
    borderRadius: 30,
  },
  contactAvatarPlaceholder: {
    width: 60,
    height: 60,
    borderRadius: 30,
    backgroundColor: '#0e4e96',
    alignItems: 'center',
    justifyContent: 'center',
  },
  contactAvatarPlaceholderText: {
    fontSize: 24,
    fontWeight: '600',
    color: '#ffffff',
  },
  contactInfo: {
    flex: 1,
  },
  contactName: {
    fontSize: 18,
    fontWeight: '600',
    color: '#1f2937',
    marginBottom: 4,
  },
  contactRole: {
    fontSize: 14,
    color: '#0e4e96',
    marginBottom: 8,
  },
  contactEmail: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 4,
  },
  contactPhone: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 16,
  },
  contactActions: {
    flexDirection: 'row',
    gap: 12,
  },
  contactButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#ffffff',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e5e7eb',
  },
  contactButtonText: {
    fontSize: 14,
    color: '#0e4e96',
    fontWeight: '500',
    marginLeft: 6,
  },
  offerResponseContainer: {
    backgroundColor: '#ffffff',
    margin: 16,
    borderRadius: 16,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 5,
  },
  responseTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#1f2937',
    marginBottom: 8,
    textAlign: 'center',
  },
  responseDeadline: {
    fontSize: 14,
    color: '#64748B',
    textAlign: 'center',
    marginBottom: 20,
  },
  responseButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 12,
  },
  responseButton: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 12,
    minHeight: 48,
  },
  rejectButton: {
    backgroundColor: '#dc2626',
  },
  negotiateButton: {
    backgroundColor: '#f59e0b',
  },
  acceptButton: {
    backgroundColor: '#10b981',
  },
  rejectButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 6,
  },
  negotiateButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 6,
  },
  acceptButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
    marginLeft: 6,
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
  },
  loadingText: {
    fontSize: 16,
    color: '#64748B',
    marginTop: 16,
    fontWeight: '500',
  },
  errorContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 32,
  },
  errorTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#dc2626',
    marginTop: 16,
    marginBottom: 8,
  },
  errorDescription: {
    fontSize: 16,
    color: '#64748B',
    textAlign: 'center',
    lineHeight: 24,
    marginBottom: 24,
  },
  retryButton: {
    backgroundColor: '#0e4e96',
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
    marginBottom: 12,
  },
  retryButtonText: {
    color: '#ffffff',
    fontSize: 16,
    fontWeight: '600',
  },
  goBackButton: {
    backgroundColor: '#f3f4f6',
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
  },
  goBackButtonText: {
    color: '#64748B',
    fontSize: 16,
    fontWeight: '500',
  },
  bottomPadding: {
    height: 32,
  },
});
